/**
 * Caller-details form card.
 *
 * Replaces the voice spell-back capture for name + organisation + email
 * — typing eliminates the email-mishearing failure that submit_brief
 * and confirm_briefing kept hitting in production.
 *
 * Lifecycle: server emits `request_caller_details` → NavStack pushes
 * the form → caller fills + submits → component dispatches the
 * `merlin:caller-details-submitted` CustomEvent on document → App
 * forwards it through the authenticated control socket. The form stays
 * locked until the server explicitly accepts the identity, and can be
 * unlocked with a retry message if delivery is not acknowledged.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export interface CallerDetailsSubmission {
  name: string;
  email: string;
  organisation: string;
}

export class FormCard {
  private readonly root: HTMLElement;
  private locked = false;

  constructor(root: HTMLElement) {
    this.root = root;
    this.root.classList.add('caller-form-overlay');
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-modal', 'true');
    this.root.setAttribute('aria-labelledby', 'caller-form-title');
    this.root.setAttribute('aria-hidden', 'true');
  }

  show(): void {
    this.locked = false;
    this.root.innerHTML = `
      <form class="caller-form-sheet" novalidate>
        <div class="caller-form-crest">◇</div>
        <h2 class="caller-form-title" id="caller-form-title">Quick details for the team</h2>
        <p class="caller-form-sub">So we know who's calling. Type rather than spell — Merlin will keep going as soon as you submit.</p>
        <label class="caller-form-field">
          <span>Name</span>
          <input name="name" type="text" autocomplete="name" required spellcheck="false" aria-describedby="caller-form-guidance caller-form-feedback" />
        </label>
        <label class="caller-form-field">
          <span>Organisation</span>
          <input name="organisation" type="text" autocomplete="organization" required spellcheck="false" aria-describedby="caller-form-guidance caller-form-feedback" />
        </label>
        <label class="caller-form-field">
          <span>Email</span>
          <input name="email" type="email" autocomplete="email" required spellcheck="false" aria-describedby="caller-form-guidance caller-form-feedback" />
        </label>
        <p class="caller-form-guidance" id="caller-form-guidance">Enter your name, organisation, and a valid email address.</p>
        <p class="caller-form-feedback" id="caller-form-feedback" role="status" aria-live="polite"></p>
        <button class="caller-form-submit" type="submit" disabled>Submit</button>
      </form>
    `;
    this.root.classList.add('visible');
    this.root.setAttribute('aria-hidden', 'false');
    this.bindEvents();
    // Focus the first field once the overlay transitions in.
    window.setTimeout(() => {
      const first = this.root.querySelector<HTMLInputElement>('input[name="name"]');
      first?.focus();
    }, 60);
  }

  clear(): void {
    this.hide();
    this.root.innerHTML = '';
    this.locked = false;
  }

  hide(): void {
    this.root.classList.remove('visible');
    this.root.classList.remove('reprompt');
    this.root.setAttribute('aria-hidden', 'true');
  }

  /** Soft pulse + tint when the bridge fires the 30s reprompt. */
  flashReprompt(): void {
    this.root.classList.add('reprompt');
  }

  accept(): void {
    const submit = this.root.querySelector<HTMLButtonElement>('.caller-form-submit');
    const feedback = this.root.querySelector<HTMLElement>('.caller-form-feedback');
    if (submit) submit.textContent = 'Submitted';
    if (feedback) {
      feedback.setAttribute('role', 'status');
      feedback.textContent = 'Details received.';
    }
  }

  reject(message: string): void {
    this.locked = false;
    const form = this.root.querySelector<HTMLFormElement>('.caller-form-sheet');
    if (!form) return;
    const inputs = form.querySelectorAll<HTMLInputElement>('input');
    const submit = form.querySelector<HTMLButtonElement>('.caller-form-submit');
    const feedback = form.querySelector<HTMLElement>('.caller-form-feedback');
    inputs.forEach((input) => { input.disabled = false; });
    if (submit) {
      submit.textContent = 'Submit';
      submit.disabled = false;
    }
    if (feedback) {
      feedback.setAttribute('role', 'alert');
      feedback.textContent = message;
    }
    submit?.focus();
  }

  private bindEvents(): void {
    const form = this.root.querySelector<HTMLFormElement>('.caller-form-sheet');
    if (!form) return;
    const submit = form.querySelector<HTMLButtonElement>('.caller-form-submit');
    const inputs = form.querySelectorAll<HTMLInputElement>('input');
    const validate = () => {
      const name = (form.elements.namedItem('name') as HTMLInputElement | null)?.value.trim() ?? '';
      const org = (form.elements.namedItem('organisation') as HTMLInputElement | null)?.value.trim() ?? '';
      const email = (form.elements.namedItem('email') as HTMLInputElement | null)?.value.trim() ?? '';
      const ok = name.length > 0 && org.length > 0 && EMAIL_RE.test(email);
      if (submit) submit.disabled = !ok || this.locked;
    };
    inputs.forEach((input) => input.addEventListener('input', validate));
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      if (this.locked) return;
      const detail: CallerDetailsSubmission = {
        name: (form.elements.namedItem('name') as HTMLInputElement).value.trim(),
        organisation: (form.elements.namedItem('organisation') as HTMLInputElement).value.trim(),
        email: (form.elements.namedItem('email') as HTMLInputElement).value.trim(),
      };
      if (!detail.name || !detail.organisation || !EMAIL_RE.test(detail.email)) return;
      this.locked = true;
      inputs.forEach((input) => (input.disabled = true));
      if (submit) {
        submit.disabled = true;
        submit.textContent = 'Submitting…';
      }
      const feedback = form.querySelector<HTMLElement>('.caller-form-feedback');
      if (feedback) {
        feedback.setAttribute('role', 'status');
        feedback.textContent = 'Sending securely…';
      }
      this.root.classList.remove('reprompt');
      document.dispatchEvent(
        new CustomEvent<CallerDetailsSubmission>('merlin:caller-details-submitted', {
          detail,
          bubbles: true,
        }),
      );
    });
  }
}
